import React from 'react';
import { CopyOutlined } from '@ant-design/icons';
import { QuickContract } from '../../models/OrderContract';
import {
  ContractListStatus,
  getContractStatusColor,
  getContractStatusLabel,
  getPartySignStatusColor,
  getPartySignStatusLabel,
  getQuickContractListStatus,
  getQuickPartyDisplayName,
  isQuickPosterSigned,
  isQuickSignerSigned,
} from '../../utils/ContractUtil';

interface QuickContractAppModalProps {
  contract: QuickContract;
  currentUserId?: string;
  onClose: () => void;
}

const QuickContractAppModal: React.FC<QuickContractAppModalProps> = ({
  contract,
  currentUserId,
  onClose,
}) => {
  const status = getQuickContractListStatus(contract, currentUserId);
  const cancelled = status === ContractListStatus.cancelled;
  const posterSigned = isQuickPosterSigned(contract);
  const signerSigned = isQuickSignerSigned(contract);

  const handleCopyNo = async () => {
    await navigator.clipboard.writeText(contract.contractNo);
    alert('合约编号已复制到剪贴板');
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '375px',
          maxWidth: '100%',
          height: 'min(720px, 90vh)',
          background: '#111',
          borderRadius: '36px',
          padding: '10px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            flex: 1,
            background: '#fff',
            borderRadius: '28px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '10px 16px 8px',
              background: '#fff',
              borderBottom: '1px solid #f0f0f0',
            }}
          >
            <div
              style={{
                width: '120px',
                height: '4px',
                background: '#e8e8e8',
                borderRadius: '2px',
                margin: '0 auto 10px',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ minWidth: '40px' }} />
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#333',
                }}
              >
                {cancelled ? '闪电合约详情-已作废' : '闪电合约详情'}
              </div>
              <button
                type="button"
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  padding: '4px 0',
                  cursor: 'pointer',
                  fontSize: '15px',
                  color: '#666',
                  minWidth: '40px',
                  textAlign: 'right',
                }}
              >
                关闭
              </button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', background: '#fff' }}>
            <div style={{ padding: '16px 16px 0' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: '18px',
                    fontWeight: '600',
                    color: getContractStatusColor(status),
                  }}
                >
                  {getContractStatusLabel(status)}
                </span>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {contract.contractNo}
                </span>
                <button
                  onClick={handleCopyNo}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '2px',
                  }}
                >
                  <CopyOutlined style={{ fontSize: '14px' }} />
                </button>
              </div>

              <SignatureRow
                title="发起人"
                name={getQuickPartyDisplayName(contract, 'poster', currentUserId)}
                signed={posterSigned}
                signTime={contract.posterSignTime}
                signatureUrl={contract.posterSignUrl}
              />
              <div style={{ height: '16px' }} />
              <SignatureRow
                title="签署人"
                name={getQuickPartyDisplayName(contract, 'signer', currentUserId)}
                signed={signerSigned}
                signTime={contract.signerSignTime}
                signatureUrl={contract.signerSignUrl}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '20px 0 8px',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                  }}
                >
                  合约详情
                </span>
                <span
                  style={{
                    flex: 1,
                    textAlign: 'right',
                    fontSize: '12px',
                    color: '#1890ff',
                  }}
                >
                  闪电合约同样具备法律效力。
                </span>
              </div>
            </div>

            <div style={{ padding: '0 16px 16px' }}>
              <div
                style={{
                  background: '#FBF9F4',
                  border: '1px solid #E6E0D4',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  minHeight: '280px',
                  padding: '14px 16px 16px',
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    color: contract.content?.trim() ? '#3D3832' : '#999',
                    lineHeight: 1.625,
                    letterSpacing: '0.15px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {contract.content?.trim() || '暂无合约内容'}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '12px 16px 16px',
              background: '#fff',
              borderTop: '1px solid #f0f0f0',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
              创建、签署、作废等操作请前往 APP 完成
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SignatureRow: React.FC<{
  title: string;
  name: string;
  signed: boolean;
  signTime: unknown;
  signatureUrl: unknown;
}> = ({ title, name, signed, signTime, signatureUrl }) => {
  const timeStr =
    typeof signTime === 'string'
      ? signTime
      : signTime instanceof Date
        ? signTime.toLocaleString()
        : '';
  const url = typeof signatureUrl === 'string' ? signatureUrl : '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '14px', color: '#666', flexShrink: 0 }}>
          {title}
        </span>
        <span style={{ fontSize: '14px', color: '#333' }}>{name}</span>
        <span style={{ flex: 1 }} />
        {signed && url ? (
          <img
            src={url}
            alt="签章"
            style={{ width: '88px', height: '44px', objectFit: 'contain' }}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span
            style={{
              fontSize: '13px',
              color: getPartySignStatusColor(signed),
            }}
          >
            {getPartySignStatusLabel(signed)}
          </span>
        )}
      </div>
      {timeStr && (
        <div
          style={{
            fontSize: '12px',
            color: '#999',
            marginTop: '4px',
          }}
        >
          {timeStr}
        </div>
      )}
    </div>
  );
};

export default QuickContractAppModal;
